import React, { useState, useMemo } from 'react';
import PositionLedgerRow from './PositionLedgerRow';

/**
 * PositionLedgerTable - Display positions in chronological table
 * Supports both stocks and options with appropriate columns
 */
function PositionLedgerTable({ positions, assetClass }) {
  const [sortBy, setSortBy] = useState('first_entry_time');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'closed'

  const isOption = assetClass === 'option';

  // Filter positions
  const filteredPositions = useMemo(() => {
    if (filterStatus === 'all') return positions;
    return positions.filter(p => p.status === filterStatus);
  }, [positions, filterStatus]);

  // Sort positions
  const sortedPositions = useMemo(() => {
    return [...filteredPositions].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle numeric sorts
      if (sortBy.includes('_num') || sortBy === 'quantity') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      // Handle time sorts
      if (sortBy.includes('_time') || sortBy.includes('_at')) {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [filteredPositions, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <i className="bi bi-arrow-down-up ms-1 text-muted"></i>;
    return sortOrder === 'asc' 
      ? <i className="bi bi-arrow-up ms-1"></i>
      : <i className="bi bi-arrow-down ms-1"></i>;
  };

  if (positions.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <i className="bi bi-inbox" style={{ fontSize: '2rem' }}></i>
        <p className="mt-2">No positions</p>
      </div>
    );
  }

  return (
    <div className="position-ledger-table">
      {/* Filters */}
      <div className="table-filters mb-2">
        <div className="btn-group btn-group-sm" role="group">
          <button 
            className={`btn ${filterStatus === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilterStatus('all')}
          >
            All ({positions.length})
          </button>
          <button 
            className={`btn ${filterStatus === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilterStatus('active')}
          >
            Active ({positions.filter(p => p.status === 'active').length})
          </button>
          <button 
            className={`btn ${filterStatus === 'closed' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilterStatus('closed')}
          >
            Closed ({positions.filter(p => p.status === 'closed').length})
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-sm table-hover ledger-table">
          <thead className="table-dark sticky-top">
            <tr>
              {/* Status */}
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                Status <SortIcon field="status" />
              </th>

              {/* Symbol */}
              <th onClick={() => handleSort('symbol')} style={{ cursor: 'pointer' }}>
                Symbol <SortIcon field="symbol" />
              </th>

              {/* Option-specific columns */}
              {isOption && (
                <>
                  <th>Type</th>
                  <th onClick={() => handleSort('strike')} style={{ cursor: 'pointer' }}>
                    Strike <SortIcon field="strike" />
                  </th>
                  <th onClick={() => handleSort('expiration')} style={{ cursor: 'pointer' }}>
                    Expiry <SortIcon field="expiration" />
                  </th>
                </>
              )}

              {/* Direction & Quantity */}
              <th onClick={() => handleSort('direction')} style={{ cursor: 'pointer' }}>
                Dir <SortIcon field="direction" />
              </th>
              <th onClick={() => handleSort('quantity')} style={{ cursor: 'pointer' }}>
                Qty <SortIcon field="quantity" />
              </th>

              {/* Entry Details */}
              <th onClick={() => handleSort('avg_entry_price')} style={{ cursor: 'pointer' }}>
                Entry$ <SortIcon field="avg_entry_price" />
              </th>
              <th>Entry Fill</th>
              <th>E_Typ</th>
              <th>E_Lmt</th>
              <th>E_Slp</th>

              {/* Exit Details (for closed positions) */}
              <th>Exit$</th>
              <th>Exit Fill</th>
              <th>X_Typ</th>
              <th>X_Lmt</th>
              <th>X_Slp</th>

              {/* P&L */}
              <th onClick={() => handleSort('total_pnl_num')} style={{ cursor: 'pointer' }}>
                P&L($) <SortIcon field="total_pnl_num" />
              </th>

              {/* Bracket */}
              <th>RR</th>
              <th>TP</th>
              <th>SL Stop</th>
              <th>SL Lmt</th>

              {/* Timing */}
              <th onClick={() => handleSort('hold_time_seconds')} style={{ cursor: 'pointer' }}>
                Hold <SortIcon field="hold_time_seconds" />
              </th>
              <th onClick={() => handleSort('first_entry_time')} style={{ cursor: 'pointer' }}>
                Submit <SortIcon field="first_entry_time" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedPositions.map((position, idx) => (
              <PositionLedgerRow 
                key={`${position.symbol}-${idx}`}
                position={position}
                isOption={isOption}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Row count */}
      <div className="table-footer text-muted small">
        Showing {sortedPositions.length} of {positions.length} positions
      </div>
    </div>
  );
}

export default PositionLedgerTable;